import React from 'react';
import { Box, Typography, Grid, Paper, Button, IconButton, Stack, Switch } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import { PersonOutline } from '@mui/icons-material';
import moment from 'moment';
import { IUserDetail } from '../../../../types/User';
import TruncateMiddle from '../../../../components/TruncateMiddle';
import { usePermission } from '../../../../hooks/CommonContext';
import { useTogglePlugNCharge } from '../../../../services/user/useUser';
import { getPlugNChargeBlockedReason } from '../../plugNCharge';

export interface UserInformationProps {
    userDetail: IUserDetail;
}

export const UserInformation = ({ userDetail }: UserInformationProps) => {
    const { user } = usePermission();
    const { mutateAsync: togglePlugNCharge, isLoading: isTogglingPlugNCharge } =
        useTogglePlugNCharge();

    const plugNChargeEnabled = Boolean(userDetail.plug_n_charge_enabled);
    const blockedReason = getPlugNChargeBlockedReason({
        permissions: user,
        enabled: plugNChargeEnabled,
        vehicleLicensePlate: userDetail.vehicle_license_plate,
    });

    const handleTogglePlugNCharge = async (enabled: boolean) => {
        try {
            await togglePlugNCharge({ userId: userDetail._id, enabled });
        } catch (error) {
            // the hook already surfaces the failure as a toast
        }
    };

    return (
        <Box sx={{ color: 'white', p: 4 }}>
            <Stack direction="row" alignItems="center" mb={4} spacing={1}>
                <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 32, mr: 1 }}>
                        <PersonOutline style={{ fill: '#FFD600', width: 32, height: 32 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>Personal Information</Typography>
                        <Typography variant="body2" color="gray">Get a detailed breakdown of every charging event.</Typography>
                    </Box>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Stack direction="row" spacing={2}>
                    <Button variant="outlined" startIcon={<EditIcon />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        Edit personal information
                    </Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        Download CSV
                    </Button>
                    <IconButton sx={{ borderRadius: 3, color: 'white', border: '1px solid #444', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                        <FilterListIcon />
                    </IconButton>
                </Stack>
            </Stack>
            <Paper elevation={0} sx={{ bgcolor: '#191919', borderRadius: 4, p: 4 }}>
                <Grid container spacing={4} mb={2}>
                    <Grid item lg={3}>
                        <Typography variant="body2" color="gray">Email</Typography>
                        <Typography variant="h5" fontWeight={600}>{userDetail.email ? <TruncateMiddle text={userDetail.email} minLength={25} /> : '-'}</Typography>
                    </Grid>
                    <Grid item lg={3}>
                        <Typography variant="body2" color="gray">User Name</Typography>
                        <Typography variant="h5" fontWeight={600}>{userDetail.name || '-'}</Typography>
                    </Grid>
                    <Grid item lg={3}>
                        <Typography variant="body2" color="gray">Phone number</Typography>
                        <Typography variant="h5" fontWeight={600}>{userDetail.phone || '-'}</Typography>
                    </Grid>
                    <Grid item lg={3}>
                        <Typography variant="body2" color="gray">DOB</Typography>
                        <Typography variant="h5" fontWeight={600}>{moment(userDetail.dob).format('DD/MM/YYYY') || '-'}</Typography>
                    </Grid>
                    <Grid item lg={3}>
                        <Typography variant="body2" color="gray">Joined date</Typography>
                        <Typography variant="h5" fontWeight={600}>{moment(userDetail.createdAt).format('DD/MM/YYYY')}</Typography>
                    </Grid>
                    <Grid item lg={3}>
                        <Typography variant="body2" color="gray">Car brand</Typography>
                        <Typography variant="h5" fontWeight={600}>{userDetail.vehicle_brand || '-'}</Typography>
                    </Grid>
                    <Grid item lg={3}>
                        <Typography variant="body2" color="gray">Car model</Typography>
                        <Typography variant="h5" fontWeight={600}>{userDetail.vehicle_model || '-'}</Typography>
                    </Grid>
                    <Grid item lg={3}>
                        <Typography variant="body2" color="gray">Car plate</Typography>
                        <Typography variant="h5" fontWeight={600}>{userDetail.vehicle_license_plate || '-'}</Typography>
                    </Grid>
                    <Grid item lg={3}>
                        <Typography variant="body2" color="gray">Plug &amp; Charge</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Switch
                                checked={plugNChargeEnabled}
                                disabled={isTogglingPlugNCharge || Boolean(blockedReason)}
                                onChange={(event) => handleTogglePlugNCharge(event.target.checked)}
                                inputProps={{ 'aria-label': 'Toggle Plug and Charge for this user' }}
                            />
                            <Typography variant="h5" fontWeight={600}>
                                {plugNChargeEnabled ? 'Enabled' : 'Disabled'}
                            </Typography>
                        </Stack>
                        {blockedReason && (
                            <Typography variant="caption" color="gray">{blockedReason}</Typography>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Box>
    );
}
